import config from '@/config';

type IpError = 'timeout' | 'failure';

const errorDescriptions: {[key in IpError]: string} = {
  timeout: 'IP detection took too long.',
  failure: 'IP could not be found.',
};

// Simple regex to verify that the server isn't malfunctioning and returning a
// default status/setup page. As long as it sort of looks like an IPv4/IPv6, it
// should be safe to use the response.
const ipRegex = /^(([0-9.]+)+\.[0-9]+)|(([a-f0-9:]+:+)+[a-f0-9]*)$/;

export const useIpStore = defineStore('ipStore', () => {
  const ip4 = ref<string>();
  const ip4Loading = ref(false);
  const ip4Error = ref<IpError>();

  const ip6 = ref<string>();
  const ip6Loading = ref(false);
  const ip6Error = ref<IpError>();

  const ipList = computed(() => {
    const ips = [];
    if (ip4.value) ips.push(ip4.value);
    if (ip6.value) ips.push(ip6.value);
    return ips;
  });

  const {translate} = useLocalisation();

  function ipLabel(ip: string | undefined, loading: boolean, error: IpError | undefined): string {
    return (
      ip ??
      (loading
        ? translate('Loading...')
        : error
          ? translate(errorDescriptions[error])
          : translate('Not loaded'))
    );
  }

  const ip4Label = computed(() => ipLabel(ip4.value, ip4Loading.value, ip4Error.value));
  const ip6Label = computed(() => ipLabel(ip6.value, ip6Loading.value, ip6Error.value));

  async function getIp(
    fetchUrl: string,
    loadingRef: Ref<boolean>,
    ipRef: Ref<string | undefined>,
    errorRef: Ref<string | undefined>,
  ) {
    if (loadingRef.value) return;

    loadingRef.value = true;
    try {
      const response = await fetch(fetchUrl, {signal: AbortSignal.timeout(8000)});
      if (!response.ok) throw Error("Couldn't load IP");

      const responseText = await response.text();
      if (!ipRegex.test(responseText)) throw Error("Doesn't look like an IP");

      ipRef.value = responseText;
      errorRef.value = undefined;
    } catch (err) {
      if (err instanceof DOMException && err.name == 'TimeoutError') {
        errorRef.value = 'timeout';
      } else {
        errorRef.value = 'failure';
      }
      ipRef.value = undefined;
    } finally {
      loadingRef.value = false;
    }
  }

  async function loadIps() {
    const promises = [];
    if (config.IP4_DETECT_URL && !ip4.value && !ip4Error.value) {
      promises.push(getIp(config.IP4_DETECT_URL, ip4Loading, ip4, ip4Error));
    }
    if (config.IP6_DETECT_URL && !ip6.value && !ip6Error.value) {
      promises.push(getIp(config.IP6_DETECT_URL, ip6Loading, ip6, ip6Error));
    }
    await Promise.all(promises);
  }

  return {ip4, ip6, ipList, loadIps, ip4Label, ip6Label, ip4Error, ip6Error};
});
